import Phaser from 'phaser'

import type {
  BossArenaPlayer,
  BossArenaProjectile,
  BossArenaState,
} from '@shared/games/protocol'
import { isBossArenaState } from '@shared/games/protocol'

const LERP = 0.32
const WORLD_W = 960
const WORLD_H = 540

function hexToColor(hex: string | undefined | null): number {
  if (typeof hex !== 'string' || !hex) return 0x94a3b8
  try {
    return Phaser.Display.Color.HexStringToColor(hex).color
  } catch {
    return 0x94a3b8
  }
}

interface PlayerView {
  container: Phaser.GameObjects.Container
  body: Phaser.GameObjects.Graphics
  shieldGfx: Phaser.GameObjects.Graphics
  hpBar: Phaser.GameObjects.Graphics
  label: Phaser.GameObjects.Text
  targetX: number
  targetY: number
  radius: number
  color: string
  alive: boolean
  hp: number
  maxHp: number
  facingDx: number
  facingDy: number
  team: string
  shielded: boolean
  shieldUntil: number
  pulseT: number
}

interface ProjectileView {
  gfx: Phaser.GameObjects.Graphics
  targetX: number
  targetY: number
  radius: number
  team: string
}

class BossArenaSpectatorScene extends Phaser.Scene {
  private players = new Map<string, PlayerView>()
  private projectiles = new Map<string, ProjectileView>()
  private banner: Phaser.GameObjects.Text | null = null
  private pendingState: BossArenaState | null = null

  constructor() {
    super('BossArenaSpectator')
  }

  create() {
    this.banner = this.add
      .text(WORLD_W / 2, 40, '', {
        fontFamily: 'Segoe UI, sans-serif',
        fontSize: '32px',
        color: '#f8fafc',
        fontStyle: 'bold',
        stroke: '#0f172a',
        strokeThickness: 4,
      })
      .setOrigin(0.5)
      .setDepth(30)

    if (this.pendingState) {
      const s = this.pendingState
      this.pendingState = null
      this.applyState(s)
    }
  }

  applyState(raw: unknown) {
    if (!isBossArenaState(raw)) return
    if (!this.sys?.isActive()) {
      this.pendingState = raw
      return
    }
    const state = raw

    const seen = new Set<string>()
    for (const p of state.players) {
      seen.add(p.playerKey)
      this.upsertPlayer(p)
    }
    for (const [key, view] of this.players) {
      if (!seen.has(key)) {
        view.container.destroy(true)
        this.players.delete(key)
      }
    }

    const seenProj = new Set<string>()
    for (const proj of state.projectiles) {
      seenProj.add(proj.id)
      this.upsertProjectile(proj)
    }
    for (const [id, view] of this.projectiles) {
      if (!seenProj.has(id)) {
        view.gfx.destroy()
        this.projectiles.delete(id)
      }
    }

    if (!this.banner) return
    if (state.phase === 'countdown') {
      const left = state.countdownEndsAt
        ? Math.max(0, Math.ceil((state.countdownEndsAt - Date.now()) / 1000))
        : 3
      this.banner.setText(String(Math.max(1, left)))
    } else if (state.phase === 'results') {
      this.banner.setText(state.winnerLabel ?? 'Round over')
    } else if (state.phase === 'lobby') {
      this.banner.setText('')
    } else {
      this.banner.setText('')
    }
  }

  private upsertPlayer(p: BossArenaPlayer) {
    let view = this.players.get(p.playerKey)
    if (!view) {
      const container = this.add.container(p.x, p.y)
      const shieldGfx = this.add.graphics()
      const body = this.add.graphics()
      const hpBar = this.add.graphics()
      const label = this.add
        .text(0, 0, p.displayName, {
          fontFamily: 'Segoe UI, sans-serif',
          fontSize: '12px',
          color: '#f8fafc',
          stroke: '#0f172a',
          strokeThickness: 3,
        })
        .setOrigin(0.5, 1)
      container.add([shieldGfx, body, hpBar, label])
      view = {
        container,
        body,
        shieldGfx,
        hpBar,
        label,
        targetX: p.x,
        targetY: p.y,
        radius: p.radius,
        color: p.color,
        alive: p.alive,
        hp: p.hp,
        maxHp: p.maxHp,
        facingDx: p.facingDx,
        facingDy: p.facingDy,
        team: p.team,
        shielded: false,
        shieldUntil: 0,
        pulseT: 0,
      }
      this.players.set(p.playerKey, view)
    }

    view.targetX = p.x
    view.targetY = p.y
    view.radius = p.radius
    view.color = p.color
    view.alive = p.alive
    view.hp = p.hp
    view.maxHp = p.maxHp
    view.facingDx = p.facingDx
    view.facingDy = p.facingDy
    view.team = p.team
    view.shieldUntil =
      typeof p.shieldUntil === 'number' ? p.shieldUntil : 0
    view.shielded =
      p.team === 'boss' && view.shieldUntil > Date.now()
    view.label.setText(p.displayName)
    this.paintPlayer(view)
    this.paintShieldField(view)
  }

  private paintPlayer(view: PlayerView) {
    const g = view.body
    g.clear()
    const alpha = view.alive ? 1 : 0.3
    const color = hexToColor(view.color)
    const r = view.radius

    g.fillStyle(0x000000, 0.28 * alpha)
    g.fillEllipse(0, r * 0.55, r * 1.4, r * 0.45)
    g.fillStyle(color, alpha)
    g.fillCircle(0, 0, r)
    if (view.team === 'boss') {
      g.lineStyle(3, 0xfbbf24, alpha)
      g.strokeCircle(0, 0, r)
    }

    const len = Math.hypot(view.facingDx, view.facingDy) || 1
    g.fillStyle(0xffffff, 0.9 * alpha)
    g.fillCircle(
      (view.facingDx / len) * r * 0.55,
      (view.facingDy / len) * r * 0.55,
      Math.max(3, r * 0.18),
    )

    const bar = view.hpBar
    bar.clear()
    const bw = Math.max(28, r * 1.6)
    const by = -r - 10
    const pct = view.maxHp > 0 ? Math.max(0, view.hp / view.maxHp) : 0
    bar.fillStyle(0x0f172a, 0.75)
    bar.fillRect(-bw / 2, by, bw, 4)
    bar.fillStyle(view.team === 'boss' ? 0xf97316 : 0x22c55e, 0.95)
    bar.fillRect(-bw / 2, by, bw * pct, 4)
    view.label.setY(-r - 16)
    view.container.setAlpha(view.alive ? 1 : 0.5)
  }

  private paintShieldField(view: PlayerView) {
    const g = view.shieldGfx
    g.clear()
    if (!view.shielded || !view.alive) return

    const r = view.radius
    const pulse = 0.5 + 0.5 * Math.sin(view.pulseT * 6)
    const outer = r + 18 + pulse * 6
    const mid = r + 10 + pulse * 3

    g.fillStyle(0x38bdf8, 0.14 + pulse * 0.1)
    g.fillCircle(0, 0, outer)
    g.fillStyle(0x7dd3fc, 0.1 + pulse * 0.08)
    g.fillCircle(0, 0, mid)

    g.lineStyle(5, 0x0ea5e9, 0.55 + pulse * 0.35)
    g.strokeCircle(0, 0, outer)
    g.lineStyle(3, 0xe0f2fe, 0.7 + pulse * 0.25)
    g.strokeCircle(0, 0, mid)
    g.lineStyle(2, 0xffffff, 0.45 + pulse * 0.3)
    g.strokeCircle(0, 0, r + 4)

    for (let i = 0; i < 6; i += 1) {
      const a = (i / 6) * Math.PI * 2 + view.pulseT * 1.8
      const x1 = Math.cos(a) * (outer - 2)
      const y1 = Math.sin(a) * (outer - 2)
      const x2 = Math.cos(a) * (outer + 5 + pulse * 4)
      const y2 = Math.sin(a) * (outer + 5 + pulse * 4)
      g.lineStyle(2, 0xbae6fd, 0.65)
      g.beginPath()
      g.moveTo(x1, y1)
      g.lineTo(x2, y2)
      g.strokePath()
    }
  }

  private upsertProjectile(p: BossArenaProjectile) {
    let view = this.projectiles.get(p.id)
    if (!view) {
      const gfx = this.add.graphics()
      view = {
        gfx,
        targetX: p.x,
        targetY: p.y,
        radius: p.radius,
        team: p.team,
      }
      this.projectiles.set(p.id, view)
    }
    view.targetX = p.x
    view.targetY = p.y
    view.radius = p.radius
    view.team = p.team
    const g = view.gfx
    g.clear()
    g.fillStyle(view.team === 'boss' ? 0xfb923c : 0x7dd3fc, 1)
    g.fillCircle(0, 0, view.radius)
    g.setPosition(view.targetX, view.targetY)
  }

  update(_time: number, delta: number) {
    // update() вызывается движком Phaser внутри своего RAF-цикла — React
    // ErrorBoundary и обёртка в GamesLayer такую ошибку не ловят. Без try/catch
    // падение здесь остановит весь game loop навсегда.
    try {
      const dt = delta / 1000
      const now = Date.now()
      for (const view of this.players.values()) {
        view.container.x += (view.targetX - view.container.x) * LERP
        view.container.y += (view.targetY - view.container.y) * LERP
        const wasShielded = view.shielded
        view.shielded = view.team === 'boss' && view.shieldUntil > now
        if (view.shielded) {
          view.pulseT += dt
          this.paintShieldField(view)
        } else if (wasShielded) {
          view.shieldGfx.clear()
        }
      }
      for (const view of this.projectiles.values()) {
        view.gfx.x += (view.targetX - view.gfx.x) * LERP
        view.gfx.y += (view.targetY - view.gfx.y) * LERP
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[overlay/boss-arena] update() failed, skipping frame:', error)
    }
  }

  clearAll() {
    for (const view of this.players.values()) view.container.destroy(true)
    for (const view of this.projectiles.values()) view.gfx.destroy()
    this.players.clear()
    this.projectiles.clear()
    this.pendingState = null
    this.banner?.setText('')
  }
}

export function createBossArenaSpectator(parent: HTMLElement) {
  const scene = new BossArenaSpectatorScene()
  let destroyed = false
  const game = new Phaser.Game({
    type: Phaser.AUTO,
    parent,
    width: WORLD_W,
    height: WORLD_H,
    transparent: true,
    scene: [scene],
    scale: { mode: Phaser.Scale.FIT, autoCenter: Phaser.Scale.CENTER_BOTH },
    fps: { target: 30 },
  })

  return {
    applyState: (state: unknown) => {
      if (!destroyed && state) scene.applyState(state)
    },
    clear: () => scene.clearAll(),
    destroy: () => {
      destroyed = true
      scene.clearAll()
      game.destroy(true)
    },
  }
}
